import type { Difficulty } from '../../../games/shared/contract.ts';

import type { IqGameState } from './iqFrame.ts';

/**
 * Решения конечного автомата IQ на ТВ без DOM: что делать с кадром телефона, когда выбор сменился. Движок
 * (`IqWebDisplayEngine`) только исполняет ответ; здесь всё, что можно проверить тестом.
 */
export type IqWebPhase = 'lobby' | 'calibration' | 'playing' | 'paused' | 'gameover';

/** Что движок делает со страницей и экраном по кадру `gameState`. */
export type IqWebStep = 'ignore' | 'restart' | 'calibrate' | 'pause' | 'resume' | 'play';

export interface IqWebTransition {
  phase: IqWebPhase;
  sawStarted: boolean;
  step: IqWebStep;
}

export interface IqWebSetup {
  topicId: number | null;
  difficulty: Difficulty;
}

/** Страница офлайн-вопросов сложность не читает (`parseTvSetup` — всегда EASY): смена её ничего не меняет. */
export function effectiveDifficulty({ topicId, difficulty }: IqWebSetup): Difficulty {
  return topicId === null ? 'EASY' : difficulty;
}

/** Выбор на телефоне сменился так, что страницу надо собрать заново. */
export function setupChanged(prev: IqWebSetup, next: IqWebSetup): boolean {
  return prev.topicId !== next.topicId || effectiveDifficulty(prev) !== effectiveDifficulty(next);
}

/**
 * Пересобранную страницу надо стартовать, если раунд идёт (`playing`) или стоит на паузе, а телефон уже играет.
 * Кадр подготовки при паузе — телефон в калибровке: новый раунд начнётся по `start-game`, а не сам.
 */
export function startsAfterRebuild(phase: IqWebPhase, gameState: IqGameState): boolean {
  return phase === 'playing' || (phase === 'paused' && gameState !== 'PREPARING');
}

/**
 * Переход по кадру состояния. Канал без гарантий доставки: телефон повторяет кадры, повтор не меняет ничего
 * (`ignore`/`calibrate`/`play`), действие — только при смене фазы.
 */
export function transition(phase: IqWebPhase, sawStarted: boolean, gameState: IqGameState): IqWebTransition {
  const same = (step: IqWebStep): IqWebTransition => ({ phase, sawStarted, step });
  switch (gameState) {
  case 'PREPARING':
    // Запоздавший кадр подготовки сразу после `start-game`, до первого кадра игры, рестартом не считаем.
    if (phase === 'playing' && !sawStarted) return same('ignore');
    if (phase !== 'calibration') return { phase: 'calibration', sawStarted: false, step: 'restart' };
    return same('calibrate');
  case 'PAUSED':
    return phase === 'playing' ? { phase: 'paused', sawStarted, step: 'pause' } : same('ignore');
  case 'STARTED': {
    const started = phase === 'playing' || phase === 'paused' ? true : sawStarted;
    if (phase === 'paused') return { phase: 'playing', sawStarted: started, step: 'resume' };
    return { phase, sawStarted: started, step: phase === 'playing' ? 'play' : 'ignore' };
  }
  default:
    return same('ignore');
  }
}
